import React, { useState } from "react";
import { Link } from "react-router-dom";
import styles from "./HireEmployees.module.css";
import {
  FiSearch, FiUsers, FiBriefcase, FiZap, FiShield, FiTrendingUp,
  FiCheck, FiArrowRight, FiStar, FiBarChart2, FiTarget, FiClock
} from "react-icons/fi";

const features = [
  {
    icon: <FiSearch />,
    title: "Smart Candidate Search",
    desc: "Use AI-powered filters to find candidates by skill, experience, location, and more. No more sifting through irrelevant profiles.",
  },
  {
    icon: <FiZap />,
    title: "Post Jobs in Minutes",
    desc: "Create and publish job listings in under 5 minutes with our intuitive job posting wizard. Reach thousands of qualified candidates immediately.",
  },
  {
    icon: <FiTarget />,
    title: "Targeted Job Promotion",
    desc: "Boost your listings to appear at the top of search results and reach the most relevant candidates faster.",
  },
  {
    icon: <FiBarChart2 />,
    title: "Hiring Analytics",
    desc: "Track views, applications, and candidate quality with detailed analytics. Make data-driven hiring decisions.",
  },
  {
    icon: <FiShield />,
    title: "Verified Profiles",
    desc: "All candidate profiles are verified. Access background-checked, credential-verified professionals with confidence.",
  },
  {
    icon: <FiClock />,
    title: "Fast Turnaround",
    desc: "Reduce your time-to-hire with our automated screening tools and pre-scheduled interview workflows.",
  },
];

const steps = [
  {
    num: "01",
    title: "Create Your Employer Account",
    desc: "Sign up for free and set up your company profile. Add your logo, description, culture, and open positions.",
  },
  {
    num: "02",
    title: "Post Your Job Openings",
    desc: "Use our guided wizard to create detailed job listings. Specify skills, experience, salary, and other requirements.",
  },
  {
    num: "03",
    title: "Review Applications",
    desc: "Candidates apply directly through the platform. Review profiles, resumes, and cover letters in one place.",
  },
  {
    num: "04",
    title: "Hire with Confidence",
    desc: "Schedule interviews, make offers, and onboard your new hire — all managed through your employer dashboard.",
  },
];

const plans = [
  {
    name: "Starter",
    price: "₹4,999",
    period: "/ month",
    desc: "Perfect for small businesses and startups",
    features: [
      "3 active job postings",
      "Up to 50 applications/job",
      "Basic candidate search",
      "Email support",
      "7-day trial included",
    ],
    cta: "Get Started Free",
    highlighted: false,
  },
  {
    name: "Professional",
    price: "₹12,999",
    period: "/ month",
    desc: "For growing teams with regular hiring needs",
    features: [
      "15 active job postings",
      "Unlimited applications",
      "Advanced candidate search",
      "Featured job listings",
      "Analytics dashboard",
      "Priority support",
    ],
    cta: "Start Hiring Now",
    highlighted: true,
  },
  {
    name: "Enterprise",
    price: "Custom",
    period: "",
    desc: "For large organizations with complex hiring",
    features: [
      "Unlimited job postings",
      "Dedicated account manager",
      "Custom integrations (ATS)",
      "Bulk job import",
      "Advanced analytics & reports",
      "SLA guarantee",
    ],
    cta: "Contact Sales",
    highlighted: false,
  },
];

const testimonials = [
  {
    company: "TechCorp India",
    logo: "T",
    color: "#5540C7",
    quote: "We hired 12 engineers in one month using Blockenzyme. The candidate quality is phenomenal.",
    person: "Vikram Nair, HR Head",
  },
  {
    company: "FinScale",
    logo: "F",
    color: "#2874f0",
    quote: "The analytics dashboard helped us optimize our job descriptions and reduced our cost-per-hire by 40%.",
    person: "Sneha Kapoor, Talent Lead",
  },
  {
    company: "DesignStudio",
    logo: "D",
    color: "#e23744",
    quote: "Best recruiting platform we've used. The verified profiles save us so much screening time.",
    person: "Arjun Bose, Founder",
  },
];

const HireEmployees = () => {
  const [billingAnnual, setBillingAnnual] = useState(false);

  return (
    <div className={styles.page}>
      {/* Hero */}
      <section className={styles.hero}>
        <div className={styles.heroInner}>
          <div className={styles.heroBadge}>
            <FiTrendingUp /> &nbsp; Trusted by 10,000+ companies
          </div>
          <h1 className={styles.heroTitle}>
            Find the Right Talent, <span className={styles.highlight}>Fast</span>
          </h1>
          <p className={styles.heroSubtitle}>
            Connect with thousands of pre-vetted, skilled professionals. Post jobs, search candidates, and hire with confidence — all in one place.
          </p>
          <div className={styles.heroBtns}>
            <button className={styles.heroPrimaryBtn}>
              <FiBriefcase /> Post a Job — It's Free
            </button>
            <Link to="/find-jobs" className={styles.heroSecondaryBtn}>
              <FiSearch /> Browse Candidates
            </Link>
          </div>
          <div className={styles.heroStats}>
            <div className={styles.heroStat}>
              <span className={styles.heroStatNum}>5,00,000+</span>
              <span className={styles.heroStatLabel}>Active Candidates</span>
            </div>
            <div className={styles.heroStatDiv} />
            <div className={styles.heroStat}>
              <span className={styles.heroStatNum}>48 hrs</span>
              <span className={styles.heroStatLabel}>Avg. First Application</span>
            </div>
            <div className={styles.heroStatDiv} />
            <div className={styles.heroStat}>
              <span className={styles.heroStatNum}>94%</span>
              <span className={styles.heroStatLabel}>Employer Satisfaction</span>
            </div>
          </div>
        </div>
      </section>

      {/* Features */}
      <section className={styles.section}>
        <div className={styles.sectionInner}>
          <div className={styles.sectionHeader}>
            <h2 className={styles.sectionTitle}>Everything You Need to Hire Smarter</h2>
            <p className={styles.sectionSubtitle}>Powerful tools designed to simplify every step of your recruitment process</p>
          </div>
          <div className={styles.featuresGrid}>
            {features.map((f, i) => (
              <div key={i} className={styles.featureCard}>
                <div className={styles.featureIcon}>{f.icon}</div>
                <h4 className={styles.featureTitle}>{f.title}</h4>
                <p className={styles.featureDesc}>{f.desc}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* How It Works */}
      <section className={styles.howSection}>
        <div className={styles.sectionInner}>
          <div className={styles.sectionHeader}>
            <h2 className={styles.sectionTitle}>Start Hiring in 4 Simple Steps</h2>
            <p className={styles.sectionSubtitle}>From account setup to your first hire — it's straightforward and fast</p>
          </div>
          <div className={styles.stepsGrid}>
            {steps.map((step, i) => (
              <div key={i} className={styles.stepCard}>
                <div className={styles.stepNumBadge}>{step.num}</div>
                {i < steps.length - 1 && <div className={styles.stepConnector}><FiArrowRight /></div>}
                <h4 className={styles.stepTitle}>{step.title}</h4>
                <p className={styles.stepDesc}>{step.desc}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Pricing */}
      <section className={styles.section}>
        <div className={styles.sectionInner}>
          <div className={styles.sectionHeader}>
            <h2 className={styles.sectionTitle}>Simple, Transparent Pricing</h2>
            <p className={styles.sectionSubtitle}>No hidden fees. Cancel anytime. Start for free.</p>
          </div>
          <div className={styles.billingToggle}>
            <span className={!billingAnnual ? styles.billingActive : ""}>Monthly</span>
            <button
              className={`${styles.toggleBtn} ${billingAnnual ? styles.toggleActive : ""}`}
              onClick={() => setBillingAnnual(!billingAnnual)}
            >
              <span className={styles.toggleThumb} />
            </button>
            <span className={billingAnnual ? styles.billingActive : ""}>
              Annual <span className={styles.saveBadge}>Save 20%</span>
            </span>
          </div>
          <div className={styles.plansGrid}>
            {plans.map((plan) => (
              <div key={plan.name} className={`${styles.planCard} ${plan.highlighted ? styles.planHighlighted : ""}`}>
                {plan.highlighted && <div className={styles.popularBadge}><FiStar /> Most Popular</div>}
                <div className={styles.planName}>{plan.name}</div>
                <div className={styles.planPrice}>
                  {plan.price}
                  <span className={styles.planPeriod}>{plan.period}</span>
                </div>
                <p className={styles.planDesc}>{plan.desc}</p>
                <ul className={styles.planFeatures}>
                  {plan.features.map((f) => (
                    <li key={f}>
                      <FiCheck className={styles.planCheck} />
                      {f}
                    </li>
                  ))}
                </ul>
                <button className={`${styles.planBtn} ${plan.highlighted ? styles.planBtnHighlighted : ""}`}>
                  {plan.cta}
                </button>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Testimonials */}
      <section className={styles.testimonialSection}>
        <div className={styles.sectionInner}>
          <div className={styles.sectionHeader}>
            <h2 className={styles.sectionTitle} style={{ color: "#fff" }}>What Employers Say</h2>
            <p className={styles.sectionSubtitle} style={{ color: "rgba(255,255,255,0.75)" }}>Thousands of companies trust Blockenzyme for their hiring needs</p>
          </div>
          <div className={styles.testimonialGrid}>
            {testimonials.map((t, i) => (
              <div key={i} className={styles.testimonialCard}>
                <div className={styles.testimonialStars}>
                  {[1,2,3,4,5].map((s) => <FiStar key={s} className={styles.tStar} />)}
                </div>
                <p className={styles.testimonialQuote}>"{t.quote}"</p>
                <div className={styles.testimonialFooter}>
                  <div className={styles.testimonialLogo} style={{ background: t.color + "20", color: t.color }}>{t.logo}</div>
                  <div>
                    <div className={styles.testimonialCompany}>{t.company}</div>
                    <div className={styles.testimonialPerson}>{t.person}</div>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className={styles.ctaSection}>
        <div className={styles.ctaInner}>
          <FiUsers className={styles.ctaIcon} />
          <h2 className={styles.ctaTitle}>Ready to Build Your Dream Team?</h2>
          <p className={styles.ctaSubtitle}>Join 10,000+ companies already hiring on Blockenzyme. Post your first job today — it's free!</p>
          <button className={styles.ctaBtn}>
            Get Started for Free <FiArrowRight />
          </button>
        </div>
      </section>
    </div>
  );
};

export default HireEmployees;
